import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';

import { filter, switchMap } from 'rxjs';

import { TuiRoot } from '@taiga-ui/core';

import { AuthenticationFacadeService } from '@finverity/authentication';
import { FinLayoutService } from '@finverity/platform-app/core/layout/services';
import { FinApiErrorHandler, FinEnvironmentService, NotificationsService } from '@finverity/platform-app/core/services';
import { SelectedInstrumentsSummaryPopupHostComponent } from '@finverity/platform-app/modules/instrument/features/selected-instruments-summary';
import { FvConfirmService } from '@fv/ui-kit';

@Component({
  selector: 'fin-platform-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, TuiRoot, SelectedInstrumentsSummaryPopupHostComponent],
})
export class AppComponent implements OnInit {
  private readonly titleService = inject(Title);
  private readonly environmentService = inject(FinEnvironmentService);
  private readonly authFacade = inject(AuthenticationFacadeService);
  private readonly layoutService = inject(FinLayoutService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsService = inject(NotificationsService);
  private readonly confirmDialogService = inject(FvConfirmService);
  private readonly router = inject(Router);

  readonly favIcon: HTMLLinkElement | null = document.querySelector('#appIcon');

  constructor() {
    this.initializeErrorHandler();
  }

  ngOnInit() {
    this.setTitleAndFavicon();
    this.handleAuthLayoutBackground();
    this.handlePasswordExpirationNotification();
  }

  private initializeErrorHandler(): void {
    // TODO: performance_issue - Stopping application to initialize faster
    FinApiErrorHandler.init();
  }

  private setTitleAndFavicon(): void {
    this.titleService.setTitle(this.environmentService.platform.title);
    if (this.favIcon) {
      this.favIcon.href = this.environmentService.platform.faviconUrl;
    }
  }

  private handleAuthLayoutBackground(): void {
    this.authFacade.loggedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loggedIn => {
      if (loggedIn) {
        this.layoutService.setAuthLayoutBackgroundColor();
      } else {
        this.layoutService.removeAuthLayoutBackgroundColor();
      }
    });
  }

  private handlePasswordExpirationNotification(): void {
    this.authFacade.loggedIn$
      .pipe(
        filter(loggedIn => loggedIn),
        filter(() => {
          const user = this.authFacade.userSignal();
          return !user?.is_password_never_expiring && !user?.is_service_account;
        }),
        switchMap(() =>
          this.authFacade.isInGracePeriod$.pipe(
            filter(isInGracePeriod => !!isInGracePeriod),
            switchMap(() => this.authFacade.daysUntilExpiration$),
            filter(daysUntilExpiration => daysUntilExpiration !== null && daysUntilExpiration !== undefined)
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(daysUntilExpiration => {
        this.notificationsService
          .showNotification(
            `Your password expires in ${daysUntilExpiration} days. Click [Change Password] to update it now.`,
            'warning',
            'Password Expiration'
          )
          .subscribe();

        this.confirmDialogService
          .warning({
            title: 'Password Expiration Warning',
            content: `Your password will expire in ${daysUntilExpiration} days. Would you like to change it now?`,
            submitBtnText: 'Change Password',
            cancelBtnText: 'Later',
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(confirmed => {
            if (confirmed) {
              this.router
                .navigate(['/user-profile/details'], {
                  state: {
                    activeTab: 1,
                    fromPasswordExpiration: true,
                  },
                })
                .then();
            }
          });
      });
  }
}
